// Achievement System Configuration
// Defines all achievements, their unlock conditions, and rewards

import type { Achievement, AchievementId } from '../types/learning';

export const ACHIEVEMENT_DEFINITIONS: Record<AchievementId, Omit<Achievement, 'unlockedAt' | 'progress'>> = {
  // ===== ISLAND ACHIEVEMENTS =====
  'island-first-step': {
    id: 'island-first-step',
    name: 'First Steps on the Island',
    description: 'Explored your first capability region',
    icon: '🏝️',
    category: 'island',
    tier: 'bronze',
    maxProgress: 1,
  },
  'island-explorer': {
    id: 'island-explorer',
    name: 'Island Explorer',
    description: 'Visited all 12 capability regions',
    icon: '🗺️',
    category: 'island',
    tier: 'gold',
    maxProgress: 12,
  },
  'scope-specialist': {
    id: 'scope-specialist',
    name: 'Scope Specialist',
    description: 'Mastered all capabilities within a single scope level',
    icon: '🎯',
    category: 'island',
    tier: 'silver',
    maxProgress: 3, // 3 capabilities per scope
  },
  'individual-master': {
    id: 'individual-master',
    name: 'Individual Mastery',
    description: 'Explored all Individual-scope capabilities',
    icon: '👤',
    category: 'island',
    tier: 'silver',
    maxProgress: 3,
  },
  'team-master': {
    id: 'team-master',
    name: 'Team Orchestrator',
    description: 'Explored all Team-scope capabilities',
    icon: '👥',
    category: 'island',
    tier: 'silver',
    maxProgress: 3,
  },
  'org-master': {
    id: 'org-master',
    name: 'Organizational Architect',
    description: 'Explored all Organization-scope capabilities',
    icon: '🏢',
    category: 'island',
    tier: 'silver',
    maxProgress: 3,
  },
  'ecosystem-master': {
    id: 'ecosystem-master',
    name: 'Ecosystem Navigator',
    description: 'Explored all Ecosystem-scope capabilities',
    icon: '🌐',
    category: 'island',
    tier: 'silver',
    maxProgress: 3,
  },

  // ===== MOUNTAIN ACHIEVEMENTS =====
  'base-camp-arrival': {
    id: 'base-camp-arrival',
    name: 'Base Camp Arrival',
    description: 'Reached Base Camp (1000m) - DECIDE stage',
    icon: '⛺',
    category: 'mountain',
    tier: 'bronze',
    maxProgress: 1,
  },
  'summit-seeker': {
    id: 'summit-seeker',
    name: 'Summit Seeker',
    description: 'Reached the Summit (7000m) - PRIORITIZE stage',
    icon: '🏔️',
    category: 'mountain',
    tier: 'gold',
    maxProgress: 1,
  },
  'degap-master': {
    id: 'degap-master',
    name: 'DEGAP Master',
    description: 'Progressed through all 5 DEGAP stages',
    icon: '📊',
    category: 'mountain',
    tier: 'gold',
    maxProgress: 5,
  },
  'route-explorer': {
    id: 'route-explorer',
    name: 'Route Explorer',
    description: 'Explored all 12 capability climbing routes',
    icon: '🧗',
    category: 'mountain',
    tier: 'silver',
    maxProgress: 12,
  },

  // ===== MOF ACHIEVEMENTS =====
  'architect-apprentice': {
    id: 'architect-apprentice',
    name: 'Architect Apprentice',
    description: 'Explored all 5 MOF architecture layers',
    icon: '🏗️',
    category: 'mof',
    tier: 'gold',
    maxProgress: 5,
  },
  'engine-expert': {
    id: 'engine-expert',
    name: 'Engine Expert',
    description: 'Deep dive into Engines infrastructure',
    icon: '⚙️',
    category: 'mof',
    tier: 'silver',
    maxProgress: 1,
  },
  'grammar-guru': {
    id: 'grammar-guru',
    name: 'Grammar Guru',
    description: 'Mastered the M3 Meta-Stack metadata',
    icon: '📚',
    category: 'mof',
    tier: 'silver',
    maxProgress: 4, // 4 layers of meta-stack
  },
  'model-master': {
    id: 'model-master',
    name: 'Model Master',
    description: 'Explored 20+ M2 models and frameworks',
    icon: '🎨',
    category: 'mof',
    tier: 'gold',
    maxProgress: 20,
  },
  'template-tactician': {
    id: 'template-tactician',
    name: 'Template Tactician',
    description: 'Reviewed 10+ M1 ready-to-deploy templates',
    icon: '📋',
    category: 'mof',
    tier: 'silver',
    maxProgress: 10,
  },
  'runtime-runner': {
    id: 'runtime-runner',
    name: 'Runtime Runner',
    description: 'Studied 5+ M0 real-world instances',
    icon: '🚀',
    category: 'mof',
    tier: 'bronze',
    maxProgress: 5,
  },

  // ===== GLOSSARY ACHIEVEMENTS =====
  'word-smith': {
    id: 'word-smith',
    name: 'Word Smith',
    description: 'Learned 10 glossary terms',
    icon: '📖',
    category: 'glossary',
    tier: 'bronze',
    maxProgress: 10,
  },
  'polyglot': {
    id: 'polyglot',
    name: 'Knowledge Polyglot',
    description: 'Learned 50+ glossary terms',
    icon: '🌟',
    category: 'glossary',
    tier: 'gold',
    maxProgress: 50,
  },
  'concept-connector': {
    id: 'concept-connector',
    name: 'Concept Connector',
    description: 'Explored all conceptual term families',
    icon: '🔗',
    category: 'glossary',
    tier: 'silver',
    maxProgress: 2, // Currently DEGAP and Relevance Framework families
  },

  // ===== ENGAGEMENT ACHIEVEMENTS =====
  'curious-learner': {
    id: 'curious-learner',
    name: 'Curious Learner',
    description: 'Asked the AI Navigator 10 questions',
    icon: '❓',
    category: 'engagement',
    tier: 'bronze',
    maxProgress: 10,
  },
  'knowledge-seeker': {
    id: 'knowledge-seeker',
    name: 'Knowledge Seeker',
    description: 'Asked the AI Navigator 50 questions',
    icon: '🔍',
    category: 'engagement',
    tier: 'silver',
    maxProgress: 50,
  },
  'power-user': {
    id: 'power-user',
    name: 'Power User',
    description: 'Asked the AI Navigator 100 questions',
    icon: '⚡',
    category: 'engagement',
    tier: 'gold',
    maxProgress: 100,
  },
  'search-savant': {
    id: 'search-savant',
    name: 'Search Savant',
    description: 'Used advanced search 10+ times',
    icon: '🔎',
    category: 'engagement',
    tier: 'bronze',
    maxProgress: 10,
  },

  // ===== MASTERY ACHIEVEMENTS =====
  'framework-fan': {
    id: 'framework-fan',
    name: 'Framework Fan',
    description: 'Viewed 10 detailed framework entries',
    icon: '🎯',
    category: 'mastery',
    tier: 'bronze',
    maxProgress: 10,
  },
  'deep-diver': {
    id: 'deep-diver',
    name: 'Deep Diver',
    description: 'Spent 30+ minutes exploring a single framework',
    icon: '🤿',
    category: 'mastery',
    tier: 'silver',
    maxProgress: 1,
  },
  'completionist': {
    id: 'completionist',
    name: 'The Completionist',
    description: 'Unlocked all other achievements',
    icon: '👑',
    category: 'mastery',
    tier: 'platinum',
    maxProgress: 1,
  },
  'pioneer': {
    id: 'pioneer',
    name: 'Pioneer',
    description: 'Among the first to explore the knowledge system',
    icon: '🚩',
    category: 'mastery',
    tier: 'platinum',
    maxProgress: 1,
  },
};

export const ACHIEVEMENT_POINTS: Record<Achievement['tier'], number> = {
  bronze: 10,
  silver: 25,
  gold: 50,
  platinum: 100,
};

export const getAchievementsByCategory = (category: Achievement['category']): Achievement[] => {
  return Object.values(ACHIEVEMENT_DEFINITIONS)
    .filter(a => a.category === category)
    .map(def => ({
      ...def,
      progress: 0,
    }));
};

export const getAchievementById = (id: AchievementId): Achievement => {
  const def = ACHIEVEMENT_DEFINITIONS[id];
  return {
    ...def,
    progress: 0,
  };
};

export const calculateTotalPoints = (achievements: Achievement[]): number => {
  return achievements
    .filter(a => a.unlockedAt)
    .reduce((total, a) => total + ACHIEVEMENT_POINTS[a.tier], 0);
};

export const checkAchievementUnlock = (
  achievement: Achievement,
  currentProgress: number
): boolean => {
  return currentProgress >= achievement.maxProgress;
};

